import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  computed,
  ViewChild,
  ChangeDetectorRef,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService, MenuItem } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { MenuModule, Menu } from 'primeng/menu';
import { DialogModule } from 'primeng/dialog';
import { TextareaModule } from 'primeng/textarea';
import {
  formatCurrencyVND,
  formatDateTime,
} from '@shared/utils/date-formatter.util';
import { PermissionService } from '@app/core/services/permission.service';
import { SKUItem, SKUType } from '../../models/sku.interface';
import { SKUService } from '../../services/sku.service';
import {
  SKU_STATUSES,
  CAN_DELETE_STATUS,
  CAN_SUBMIT_STATUS,
  CAN_APPROVE_STATUS,
  getSKUStatusByCode,
} from '../../models/sku.constants';
import {
  SKU_TABLE_COLUMNS,
  SKU_DEFAULT_COLUMNS_ORDER,
  SKU_FLEXIBLE_COLUMNS_ORDER,
  SKU_ACTION_COLUMN,
  getSKUSearchConfig,
} from '../../models/sku.config';
import { SearchFormComponent } from '@shared/components/search-form/search-form.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import type { TableColumn } from '@shared/components/data-table/data-table.component';
import {
  StatusFilterComponent,
  ActionButton,
} from '@shared/components/status-filter/status-filter.component';
import { LastCategoryPipe } from '@shared/pipes/last-category.pipe';

@Component({
  selector: 'app-sku-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    TagModule,
    CheckboxModule,
    TooltipModule,
    ConfirmDialogModule,
    ToastModule,
    MenuModule,
    DialogModule,
    TextareaModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    AppButtonComponent,
    StatusFilterComponent,
    LastCategoryPipe,
  ],
  templateUrl: './sku-management.component.html',
  styleUrl: './sku-management.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService, MessageService],
})
export class SKUManagementComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly skuService = inject(SKUService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();
  formatCurrency = formatCurrencyVND;
  formatDateTime = formatDateTime;

  // Permission flags - computed from PermissionService
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.sku.view');
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.sku.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.sku.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.sku.delete');
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.sku.approve');
  });

  // Check if user has any action permissions (UPDATE or DELETE)
  hasAnyActionPermission = computed(
    () => this.canUpdate() || this.canDelete() || this.canRead(),
  );

  // Advanced table column configuration
  private readonly fromConfig = (prop: string): TableColumn | undefined =>
    SKU_TABLE_COLUMNS.find((c) => c.prop === prop);

  defaultColumns = computed((): TableColumn[] => {
    const cols: TableColumn[] = [];
    for (const key of SKU_DEFAULT_COLUMNS_ORDER) {
      const col = this.fromConfig(key);
      if (col) cols.push(col);
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = this.fromConfig(SKU_ACTION_COLUMN.ACTIONS);
      if (actionsCol) cols.push(actionsCol);
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const cols: TableColumn[] = [];

    for (const prop of SKU_FLEXIBLE_COLUMNS_ORDER) {
      const col = this.fromConfig(prop);
      if (col) {
        cols.push(col);
      }
    }

    return cols;
  });

  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  // "Others" actions menu (Approve / Reject / Delete) — kept out of the
  // actions column to avoid cramming too many text links into a narrow cell.
  @ViewChild('actionMenu') actionMenu!: Menu;
  currentMenuModel = signal<MenuItem[]>([]);

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    // Find the actual button element from PrimeNG p-button
    // p-button wraps a button element, so we need to find it
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }
  activeTab = signal<SKUType>('ordinary');
  currentPage = signal(0);
  itemsPerPage = signal(20);
  totalItems = signal(0);
  loading = signal(false);
  selectedItems = signal<SKUItem[]>([]);

  // Last-applied search form criteria, reused by loadSkuItems() so reloads
  // triggered by pagination or row actions (approve/reject/submit/delete)
  // don't silently drop the user's active filters.
  private readonly defaultSearchCriteria = {
    spuCode: null as string | null,
    skuCode: null as string | null,
    spuName: null as string | null,
    skuName: null as string | null,
    categoryIdList: [] as string[],
    status: [] as number[],
  };
  private searchCriteria = signal(this.defaultSearchCriteria);

  categoryOptions = this.skuService.categoryOptions;
  statusOptions = this.skuService.statusOptions;
  categoryTreeOptions = this.skuService.categoryTreeOptions;

  skuItems = signal<SKUItem[]>([]);

  // Action button configuration base
  private readonly primaryButtonBase: ActionButton = {
    label: 'sku_management.actions.new',
    severity: 'info',
    show: true,
    icon: 'add',
  };

  // Computed button configurations with permissions
  // Drafts are created via the "Save as Draft" action in the create/edit form,
  // not through a dedicated add flow on the Draft tab.
  primaryButton = computed(() => ({
    ...this.primaryButtonBase,
    show: this.canCreate() && this.activeTab() !== 'draft',
  }));

  // Search form configuration - computed based on active tab
  searchFields = computed(() => {
    return getSKUSearchConfig(this.activeTab(), this.statusOptions);
  });

  // Computed property to check if any selected items can be deleted
  canDeleteSelectedItems = computed(() => {
    return this.selectedItems().some((item) => this.canDeleteSku(item));
  });

  canSubmitSelectedItems = computed(() => {
    return this.selectedItems().some((item) => this.canSubmitSku(item));
  });

  // Secondary button ("Submit for Approval") configuration - computed with permissions/selection
  secondaryButton = computed(() => ({
    label: 'sku_management.actions.submit',
    severity: 'info' as const,
    show: this.canUpdate(),
    icon: 'send',
    disabled: !this.canSubmitSelectedItems(),
  }));

  constructor() {
    this.setupRouteListener();
  }

  ngOnInit(): void {
    // Subscribe to permissions loaded state to trigger change detection
    // This ensures computed signals re-evaluate when permissions are loaded
    this.permissionService.permissionsLoaded$
      .pipe(takeUntil(this.destroy$))
      .subscribe((loaded) => {
        if (loaded) {
          // Trigger change detection to update computed signals
          this.cdr.detectChanges();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Reloads using the last-applied search criteria (see searchCriteria) —
  // used for pagination and for refreshing after approve/reject/submit/delete
  // so those actions don't reset the user's active filters.
  private loadSkuItems(): void {
    // Prevent multiple simultaneous loading requests
    if (this.loading()) {
      return;
    }

    this.loading.set(true);
    const criteria = this.searchCriteria();

    const searchPayload = {
      ...criteria,
      status:
        this.activeTab() === 'draft'
          ? [SKU_STATUSES.DRAFT.code]
          : criteria.status,
      setFlag: this.setFlagForTab(this.activeTab()),
      page: this.currentPage(),
      size: this.itemsPerPage(),
    };

    this.skuService.searchSkuItemsPaginated(searchPayload).subscribe({
      next: (response) => {
        this.skuItems.set(response.content);
        this.totalItems.set(response.totalElements);
        this.currentPage.set(response.number);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading SKU items:', error);
        this.loading.set(false);
      },
    });
  }

  // Draft tab shows Draft-status SKUs across both ordinary and package SKUs,
  // so it doesn't filter by setFlag the way the ordinary/package tabs do.
  private setFlagForTab(tab: SKUType): number | null {
    switch (tab) {
      case 'ordinary':
        return 0;
      case 'package':
        return 1;
      case 'draft':
        return null;
    }
  }

  private setupRouteListener(): void {
    this.route.params.subscribe((params) => {
      const type = params['type'] as SKUType;
      const currentTab = this.activeTab();

      // Determine the target tab
      let targetTab: SKUType = 'ordinary';
      if (type === 'package' || type === 'ordinary' || type === 'draft') {
        targetTab = type;
      }

      // Always update the tab and load data on initial load or tab change
      if (currentTab !== targetTab) {
        this.activeTab.set(targetTab);
        this.loadSkuItems();
      } else if (this.skuItems().length === 0) {
        // If we're on the same tab but have no data, load it
        this.loadSkuItems();
      }
    });
  }

  onTabChange(tab: SKUType): void {
    this.activeTab.set(tab);
    this.currentPage.set(0);
    this.selectedItems.set([]);
    this.searchCriteria.set(this.defaultSearchCriteria);
    this.router.navigate(['/goods/sku', tab]);
    this.loadSkuItems();
  }

  onSearch(searchData: any): void {
    this.currentPage.set(0);
    this.searchCriteria.set({
      spuCode: searchData.productCode || null,
      skuCode: searchData.skuCode || null,
      spuName: searchData.productName || null,
      skuName: searchData.skuName || null,
      categoryIdList: [], // Removed product category handling for now
      status: this.toStatusCodes(searchData.status),
    });
    this.loadSkuItems();
  }

  onReset(): void {
    this.currentPage.set(0);
    this.searchCriteria.set(this.defaultSearchCriteria);
    this.loadSkuItems();
  }

  onPageChange(event: any): void {
    // Handle both page changes and row size changes
    // Advanced table uses 0-based page, convert to match our service
    this.currentPage.set(event.page);
    this.itemsPerPage.set(event.rows);
    this.loadSkuItems();
  }

  onSelectionChange(selection: SKUItem[]): void {
    this.selectedItems.set(selection);
  }

  // Button event handlers
  onprimaryButtonClick(): void {
    this.onAddSku();
  }

  onSecondaryButtonClick(): void {
    this.onSubmit();
  }

  onDelete(): void {
    const selected = this.selectedItems();
    const deletableItems = selected.filter((item) => this.canDeleteSku(item));

    if (deletableItems.length === 0) {
      return;
    }

    const selectedIds = deletableItems.map((item) => item.id);

    let message: string;
    if (deletableItems.length === 1) {
      message = this.translateService.instant(
        'sku_management.confirmations.delete_single',
        {
          skuName: deletableItems[0].skuName,
        },
      );
    } else {
      message = this.translateService.instant(
        'sku_management.confirmations.delete_multiple',
        {
          count: deletableItems.length,
        },
      );
    }

    this.confirmationService.confirm({
      key: 'skuConfirm',
      header: this.translateService.instant(
        'sku_management.confirmations.delete_title',
      ),
      message,
      acceptLabel: this.translateService.instant(
        'sku_management.confirmations.delete',
      ),
      rejectLabel: this.translateService.instant(
        'sku_management.confirmations.cancel',
      ),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      blockScroll: true,
      closeOnEscape: true,
      dismissableMask: true,
      accept: () => this.deleteItems(selectedIds),
    });
  }

  onSubmit(): void {
    const eligibleItems = this.selectedItems().filter((item) =>
      this.canSubmitSku(item),
    );

    if (eligibleItems.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'sku_management.messages.no_items_selected_submission',
        ),
      });
      return;
    }

    this.submitItems(eligibleItems.map((item) => item.id));
  }

  onSubmitItem(item: SKUItem): void {
    if (!this.canSubmitSku(item)) {
      return;
    }
    this.submitItems([item.id]);
  }

  private submitItems(ids: number[]): void {
    this.skuService.submitSkuItems(ids).subscribe({
      next: (success) => {
        if (success) {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'sku_management.messages.items_submitted_successfully',
            ),
          });
          this.loadSkuItems();
          this.selectedItems.set([]);
        }
      },
      error: (error) => {
        const apiMessage = error?.error?.message;
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail:
            apiMessage ||
            this.translateService.instant(
              'sku_management.messages.submit_failed',
            ),
        });
      },
    });
  }

  onAddSku(): void {
    const type = this.activeTab();

    if (type === 'draft') {
      return;
    }

    if (type === 'ordinary' || type === 'package') {
      this.router.navigate(['/goods/sku', type, 'add']);
    } else {
      // For other types, use the existing service method
      this.skuService.addSkuItem(type).subscribe({
        next: (success) => {
          if (success) {
            // Navigate to add form or show dialog
          }
        },
        error: (error) => {
          console.error('Error adding SKU:', error);
        },
      });
    }
  }

  onView(item: SKUItem): void {
    this.router.navigate(['/goods/sku', this.activeTab(), 'view', item.id]);
  }

  onEdit(item: SKUItem): void {
    this.router.navigate(['/goods/sku', this.activeTab(), 'edit', item.id]);
  }

  onDeleteItem(item: SKUItem): void {
    if (!this.canDeleteSku(item)) {
      return;
    }

    const message = this.translateService.instant(
      'sku_management.confirmations.delete_single',
      {
        skuName: item.skuName,
      },
    );

    this.confirmationService.confirm({
      key: 'skuConfirm',
      header: this.translateService.instant(
        'sku_management.confirmations.delete_title',
      ),
      message,
      acceptLabel: this.translateService.instant(
        'sku_management.confirmations.delete',
      ),
      rejectLabel: this.translateService.instant(
        'sku_management.confirmations.cancel',
      ),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      blockScroll: true,
      closeOnEscape: true,
      dismissableMask: true,
      accept: () => this.deleteItems([item.id]),
    });
  }

  private deleteItems(ids: number[]): void {
    this.skuService.deleteSkuItems(ids).subscribe({
      next: (success) => {
        if (success) {
          this.loadSkuItems();
          const remaining = this.selectedItems().filter(
            (item) => !ids.includes(item.id),
          );
          this.selectedItems.set(remaining);
        }
      },
      error: (error) => {
        console.error('Error deleting items:', error);
      },
    });
  }

  onApprove(item: SKUItem): void {
    if (!this.canApproveSku(item)) {
      return;
    }

    this.confirmationService.confirm({
      key: 'skuConfirm',
      header: this.translateService.instant(
        'sku_management.confirmations.approve_title',
      ),
      message: this.translateService.instant(
        'sku_management.confirmations.approve_single',
        { skuName: item.skuName },
      ),
      acceptLabel: this.translateService.instant(
        'sku_management.confirmations.approve',
      ),
      rejectLabel: this.translateService.instant(
        'sku_management.confirmations.cancel',
      ),
      acceptButtonStyleClass: 'p-button-success',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      blockScroll: true,
      closeOnEscape: true,
      dismissableMask: true,
      accept: () => this.auditItem(item.id, true),
    });
  }

  onReject(item: SKUItem): void {
    if (!this.canApproveSku(item)) {
      return;
    }

    this.confirmationService.confirm({
      key: 'skuConfirm',
      header: this.translateService.instant(
        'sku_management.confirmations.reject_title',
      ),
      message: this.translateService.instant(
        'sku_management.confirmations.reject_single',
        { skuName: item.skuName },
      ),
      acceptLabel: this.translateService.instant(
        'sku_management.confirmations.reject',
      ),
      rejectLabel: this.translateService.instant(
        'sku_management.confirmations.cancel',
      ),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      blockScroll: true,
      closeOnEscape: true,
      dismissableMask: true,
      accept: () => this.auditItem(item.id, false),
    });
  }

  // Independent Activate/Deactivate — only Effective SKUs can be
  // activated; any Effective SKU can be deactivated. Never touches status.
  canActivateSku(item: SKUItem): boolean {
    const statusNumber = Number.parseInt(item.status, 10);
    return statusNumber === SKU_STATUSES.EFFECTIVE.code && item.showFlag !== 1;
  }

  canDeactivateSku(item: SKUItem): boolean {
    const statusNumber = Number.parseInt(item.status, 10);
    return statusNumber === SKU_STATUSES.EFFECTIVE.code && item.showFlag === 1;
  }

  onActivateItem(item: SKUItem): void {
    if (!this.canActivateSku(item)) {
      return;
    }

    this.confirmationService.confirm({
      key: 'skuConfirm',
      header: this.translateService.instant(
        'sku_management.confirmations.activate_title',
      ),
      message: this.translateService.instant(
        'sku_management.confirmations.activate_single',
        { skuName: item.skuName },
      ),
      acceptLabel: this.translateService.instant(
        'sku_management.confirmations.activate',
      ),
      rejectLabel: this.translateService.instant(
        'sku_management.confirmations.cancel',
      ),
      acceptButtonStyleClass: 'p-button-success',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      blockScroll: true,
      closeOnEscape: true,
      dismissableMask: true,
      accept: () => this.toggleActivation([item.id], true),
    });
  }

  onDeactivateItem(item: SKUItem): void {
    if (!this.canDeactivateSku(item)) {
      return;
    }
    this.openDeactivateReasonDialog([item.id]);
  }

  // Bulk Activate/Deactivate, mirroring the existing bulk-submit pattern
  // (selectedItems() + eligibility filter + confirm).
  canActivateSelectedItems = computed(() =>
    this.selectedItems().some((item) => this.canActivateSku(item)),
  );

  canDeactivateSelectedItems = computed(() =>
    this.selectedItems().some((item) => this.canDeactivateSku(item)),
  );

  onBulkActivate(): void {
    const eligible = this.selectedItems().filter((item) =>
      this.canActivateSku(item),
    );
    if (eligible.length === 0) {
      return;
    }

    this.confirmationService.confirm({
      key: 'skuConfirm',
      header: this.translateService.instant(
        'sku_management.confirmations.activate_title',
      ),
      message: this.translateService.instant(
        'sku_management.confirmations.activate_multiple',
        { count: eligible.length },
      ),
      acceptLabel: this.translateService.instant(
        'sku_management.confirmations.activate',
      ),
      rejectLabel: this.translateService.instant(
        'sku_management.confirmations.cancel',
      ),
      acceptButtonStyleClass: 'p-button-success',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      blockScroll: true,
      closeOnEscape: true,
      dismissableMask: true,
      accept: () =>
        this.toggleActivation(
          eligible.map((item) => item.id),
          true,
        ),
    });
  }

  onBulkDeactivate(): void {
    const eligible = this.selectedItems().filter((item) =>
      this.canDeactivateSku(item),
    );
    if (eligible.length === 0) {
      return;
    }
    this.openDeactivateReasonDialog(eligible.map((item) => item.id));
  }

  // Deactivate goes through a reason-capture dialog instead of the plain
  // confirm dialog (AC: "Hiển thị confirmation dialog và yêu cầu reason nếu
  // nghiệp vụ yêu cầu"); reason is optional here since no mandatory-reason
  // business rule has been defined yet.
  showDeactivateReasonDialog = signal(false);
  deactivateReason = signal('');
  private pendingDeactivateIds: number[] = [];

  private openDeactivateReasonDialog(ids: number[]): void {
    this.pendingDeactivateIds = ids;
    this.deactivateReason.set('');
    this.showDeactivateReasonDialog.set(true);
  }

  onConfirmDeactivate(): void {
    const ids = this.pendingDeactivateIds;
    this.showDeactivateReasonDialog.set(false);
    this.toggleActivation(ids, false, this.deactivateReason().trim() || undefined);
  }

  onCancelDeactivateDialog(): void {
    this.showDeactivateReasonDialog.set(false);
    this.pendingDeactivateIds = [];
  }

  private toggleActivation(
    ids: number[],
    active: boolean,
    reason?: string,
  ): void {
    this.skuService.toggleSkuActivation(ids, active, reason).subscribe({
      next: (results) => {
        const failed = results.filter((r) => !r.success);
        if (failed.length === 0) {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              active
                ? 'sku_management.messages.activate_successful'
                : 'sku_management.messages.deactivate_successful',
            ),
          });
        } else {
          const skuCodeById = new Map(
            this.skuItems().map((item) => [item.id, item.skuCode]),
          );
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: failed
              .map((f) => `${skuCodeById.get(f.skuId) ?? f.skuId}: ${f.message}`)
              .join('; '),
          });
        }
        // Always reload from backend — never assume success locally, and a
        // partial bulk failure still needs the successful rows refreshed.
        this.loadSkuItems();
        this.selectedItems.set([]);
      },
      error: (error) => {
        const apiMessage = error?.error?.message;
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail:
            apiMessage ||
            this.translateService.instant(
              active
                ? 'sku_management.messages.activate_failed'
                : 'sku_management.messages.deactivate_failed',
            ),
        });
      },
    });
  }

  private auditItem(id: number, approve: boolean): void {
    this.skuService.auditSkuItem(id, approve).subscribe({
      next: (success) => {
        if (success) {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              approve
                ? 'sku_management.messages.approve_successful'
                : 'sku_management.messages.reject_successful',
            ),
          });
          this.loadSkuItems();
        }
      },
      error: (error) => {
        const apiMessage = error?.error?.message;
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail:
            apiMessage ||
            this.translateService.instant(
              approve
                ? 'sku_management.messages.approve_failed'
                : 'sku_management.messages.reject_failed',
            ),
        });
      },
    });
  }

  getStatusSeverity(
    status: string,
  ): 'warning' | 'success' | 'danger' | 'info' {
    const statusObj = getSKUStatusByCode(Number.parseInt(status, 10));

    if (!statusObj) return 'warning';

    switch (statusObj.code) {
      case SKU_STATUSES.EFFECTIVE.code:
        return 'success';
      case SKU_STATUSES.REJECTED.code:
      case SKU_STATUSES.EFFECTIVE_REJECTED.code:
        return 'danger';
      case SKU_STATUSES.UNDER_REVIEW.code:
      case SKU_STATUSES.EFFECTIVE_EDITING.code:
        return 'warning';
      case SKU_STATUSES.DRAFT.code:
        return 'info';
      default:
        return 'warning';
    }
  }

  getStatusDisplayText(status: string): string {
    const code = Number.parseInt(status, 10);
    const statusObj = getSKUStatusByCode(code);

    if (!statusObj) return status;

    switch (statusObj.code) {
      case SKU_STATUSES.DRAFT.code:
        return 'sku_management.status.draft';
      case SKU_STATUSES.EFFECTIVE.code:
        return 'sku_management.status.effective';
      case SKU_STATUSES.REJECTED.code:
        return 'sku_management.status.rejected';
      case SKU_STATUSES.UNDER_REVIEW.code:
        return 'sku_management.status.under_review';
      case SKU_STATUSES.EFFECTIVE_EDITING.code:
        return 'sku_management.status.effective_editing';
      case SKU_STATUSES.EFFECTIVE_REJECTED.code:
        return 'sku_management.status.effective_rejected';
      default:
        return status;
    }
  }

  getStatusStyle(status: string): any {
    const statusObj = getSKUStatusByCode(Number.parseInt(status, 10));

    if (!statusObj) return {};

    switch (statusObj.code) {
      case SKU_STATUSES.DRAFT.code:
        return {
          'background-color': '#eff6ff',
          color: '#1d4ed8',
          border: 'none',
        };
      case SKU_STATUSES.EFFECTIVE.code:
        return {
          'background-color': '#ecfdf5',
          color: '#047857',
          border: 'none',
        };
      case SKU_STATUSES.REJECTED.code:
      case SKU_STATUSES.EFFECTIVE_REJECTED.code:
        return {
          'background-color': '#fef2f2',
          color: '#dc2626',
          border: 'none',
        };
      case SKU_STATUSES.UNDER_REVIEW.code:
      case SKU_STATUSES.EFFECTIVE_EDITING.code:
        return {
          'background-color': '#fef3c7',
          color: '#a16207',
          border: 'none',
        };
      default:
        return {};
    }
  }

  canDeleteSku(item: SKUItem): boolean {
    const statusNumber = Number.parseInt(item.status, 10);
    return CAN_DELETE_STATUS.includes(statusNumber as 1 | 2);
  }

  canSubmitSku(item: SKUItem): boolean {
    const statusNumber = Number.parseInt(item.status, 10);
    return CAN_SUBMIT_STATUS.includes(statusNumber as 0 | 1 | 5);
  }

  canApproveSku(item: SKUItem): boolean {
    const statusNumber = Number.parseInt(item.status, 10);
    return CAN_APPROVE_STATUS.includes(statusNumber as 2 | 4);
  }

  canEditSku(item: SKUItem): boolean {
    const statusNumber = Number.parseInt(item.status, 10);
    // Disable edit for status 2 (Under Review) and 4 (Effective - Editing)
    return statusNumber !== 2 && statusNumber !== 4;
  }

  hasOthersActions(item: SKUItem): boolean {
    return (
      (this.canApprove() && this.canApproveSku(item)) ||
      (this.canDelete() && this.canDeleteSku(item)) ||
      this.shouldFoldEditSubmit(item)
    );
  }

  // Rejected and Effective - Rejected fold Edit/Submit into Others (both
  // actions apply together there).
  private shouldFoldEditSubmit(item: SKUItem): boolean {
    const statusNumber = Number.parseInt(item.status, 10);
    return (
      this.canUpdate() &&
      this.canEditSku(item) &&
      this.canSubmitSku(item) &&
      (statusNumber === SKU_STATUSES.REJECTED.code ||
        statusNumber === SKU_STATUSES.EFFECTIVE_REJECTED.code)
    );
  }

  canEditSkuInline(item: SKUItem): boolean {
    return this.canEditSku(item) && !this.hasOthersActions(item);
  }

  canSubmitSkuInline(item: SKUItem): boolean {
    return this.canSubmitSku(item) && !this.hasOthersActions(item);
  }

  private getOthersActionsModel(item: SKUItem): MenuItem[] {
    const menuItems: MenuItem[] = [];

    if (this.canUpdate() && this.canEditSku(item)) {
      menuItems.push({
        label: this.translateService.instant('sku_management.actions.edit'),
        command: () => this.onEdit(item),
      });
    }

    if (this.canUpdate() && this.canSubmitSku(item)) {
      menuItems.push({
        label: this.translateService.instant('sku_management.actions.submit'),
        command: () => this.onSubmitItem(item),
      });
    }

    if (this.canApprove() && this.canApproveSku(item)) {
      menuItems.push(
        {
          label: this.translateService.instant('sku_management.actions.approve'),
          command: () => this.onApprove(item),
        },
        {
          label: this.translateService.instant('sku_management.actions.reject'),
          command: () => this.onReject(item),
        },
      );
    }

    if (this.canDelete() && this.canDeleteSku(item)) {
      menuItems.push({
        label: this.translateService.instant('sku_management.actions.delete'),
        command: () => this.onDeleteItem(item),
      });
    }

    // Activate/Deactivate render as their own inline actions (see
    // actionsTemplate) rather than being folded into this menu.

    return menuItems;
  }

  toggleActionsMenu(event: Event, item: SKUItem): void {
    event.preventDefault();
    event.stopPropagation();

    this.currentMenuModel.set(this.getOthersActionsModel(item));

    if (this.actionMenu) {
      this.actionMenu.toggle(event);
    }
  }

  onImageError(event: Event): void {
    const target = event.target as HTMLImageElement;
    if (target) {
      target.style.display = 'none';
    }
  }

  // Backend and frontend status codes are identical (0-5), so this just parses the raw values.
  private toStatusCodes(status: string | string[] | undefined): number[] {
    if (!status) {
      return [];
    }

    const statusArray = Array.isArray(status) ? status : [status];
    return statusArray.map((s) => Number.parseInt(s, 10));
  }
}
